import {
  Action,
  CsrfToken,
  MicrobrandingParams,
  WelcomeErrorCode,
  WelcomeFieldError,
  ThunkAction,
  UiError,
} from '../types';
import queryString from 'query-string';
import {
  ConsentCheckboxState,
  buildMarketingConsent,
  MarketingConsent,
} from '../utilities/marketing-consent';
import { CoreApplication } from '../utilities/applications';
import { ChallengeResponse } from '../contexts/recaptcha';
import { ChallengeRequest, toChallengeRequest } from '../utilities/recaptcha';
import { SignupWelcomePageState } from '../pages/signup/welcome/hooks';
import { captureTaggedException } from '../utilities/analytics/error-reporting';
import { currentCloudType } from '../utilities/env';

export const reset = (): Action => ({
  type: 'WELCOME_SIGNUP_RESET',
});

export const welcomeRequest = (): Action => ({
  type: 'WELCOME_SIGNUP_REQUEST',
});

export const welcomeRequestSucceeded = (redirectTo: string): Action => ({
  type: 'WELCOME_SIGNUP_REQUEST_SUCCESS',
  redirectTo,
});

export const welcomeRequestFailed = (formError: WelcomeErrorCode): Action => ({
  type: 'WELCOME_SIGNUP_REQUEST_FAILED',
  formError,
});

export const showFieldErrors = (errors: WelcomeFieldError): Action => ({
  type: 'WELCOME_SIGNUP_SHOW_FIELD_ERRORS',
  errors,
});

export const welcomeSetState = (state: SignupWelcomePageState): Action => ({
  type: 'WELCOME_SIGNUP_SET_STATE',
  state,
});

export interface WelcomeParams {
  displayName: string;
  password: string;
  challengeResponse?: ChallengeResponse | null;
  token: string;
  marketingConsentState: ConsentCheckboxState;
  privacyPolicyText: string;
  microbrandingParams?: MicrobrandingParams | null;
  csrfToken: CsrfToken;
}

export interface WelcomePayload {
  'challenge-response'?: ChallengeRequest | null;
  displayName: string;
  password: string | null;
  token?: string;
  marketingConsent?: MarketingConsent;
  productHint?: string;
  display: string | null;
  product?: CoreApplication;
  castleToken?: string | null;
}

// eslint-disable-next-line max-params
export const welcome =
  (welcomeParams: WelcomeParams): ThunkAction =>
  async (dispatch, getState) => {
    const {
      displayName,
      password,
      token,
      microbrandingParams,
      challengeResponse,
      marketingConsentState,
      privacyPolicyText,
      csrfToken,
    } = welcomeParams;

    // fire castle action
    const castleToken =
      currentCloudType === 'commercial'
        ? await import('@castleio/castle-js')
            .then(async ({ createRequestToken }) => await createRequestToken())
            .catch(e => {
              console.error('castle error', e);
              captureTaggedException(e, {
                capabilities: ['capability-group--signup', 'capability-name--signup'],
                tags: {
                  castle: 'castle_request_token_error',
                },
              });
              return undefined;
            })
        : undefined;

    const qs =
      microbrandingParams && Object.keys(microbrandingParams).length
        ? `?${queryString.stringify(microbrandingParams)}`
        : '';
    dispatch(welcomeRequest());

    let payload: WelcomePayload = {
      'challenge-response': toChallengeRequest(challengeResponse),
      displayName,
      password,
      token,
      display: null,
      castleToken,
    };

    const marketingConsent = buildMarketingConsent(
      getState().marketingConsent.config,
      marketingConsentState,
      privacyPolicyText
    );
    if (marketingConsent) {
      payload = { ...payload, marketingConsent };
    }

    type WelcomeResponse = {
      fieldErrors?: WelcomeFieldError;
      uiErrors?: UiError[];
      redirectTo?: string;
    };

    return fetch(`/rest/signup/welcome${qs}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(({ fieldErrors, uiErrors, redirectTo }: WelcomeResponse) => {
        if (redirectTo) {
          dispatch(welcomeRequestSucceeded(redirectTo));
        } else {
          if (uiErrors && uiErrors.length) {
            dispatch(welcomeRequestFailed(uiErrors[0].key));
          } else if (fieldErrors && Object.keys(fieldErrors).length) {
            dispatch(showFieldErrors(fieldErrors));
          } else {
            dispatch(welcomeRequestFailed('unknown'));
          }
        }
      })
      .catch(() => {
        dispatch(welcomeRequestFailed('unknown'));
      });
  };
